import React, { forwardRef, useImperativeHandle, useMemo, useRef } from "react";
import ReactDOM from "react-dom";
import { useSnapshot } from "valtio";
import { wizardStore } from "../../../stores/wizard";
import { CircleData, Step } from "../../../types/wizard";
import SelectedColor from "../../Wizard/commonComps/ColorPicker/SelectedColor";
import ColorManager from "./ColorManager";
import { DeleteIcon, EditIcon } from "../../../utils/icons";
import './../QuickView.css';

type TooltipProps = {
  isEditing: boolean;
  stepColorId: string;
  targets: CircleData[];
  indications?: CircleData[];
  containerRef: any;
  position: { x: number; y: number };
  onRemove: (matchedCircle: CircleData) => void;
  onEdit: (matchedCircle: CircleData) => void;
};

const TOOLTIP_WIDTH = 200;
const TOOLTIP_HEIGHT = 150;
const OFFSET_X = 40;
const OFFSET_Y = 60;

const ColorTooltip = forwardRef<HTMLDivElement, TooltipProps>(
  ({
    isEditing,
    stepColorId,
    targets,
    indications,
    position,
    containerRef,
    onRemove,
    onEdit
  }, ref
  ) => {
    const wizSnap = useSnapshot(wizardStore);
    const { steps } = wizSnap;
    const tooltipRef = useRef<HTMLDivElement>(null);
    const step = steps?.find(s => s.id === stepColorId) as Step;
    const findCircle = () => {
      return targets.find(t => t.circle?.x === position.x && t.circle?.y === position.y);
    };
    const matchedCircle = useMemo(() => findCircle(), [targets, position]);
    const findIndication = indications?.find(i => i?.circle?.id === matchedCircle?.circle?.id);
    const maxWidth = matchedCircle ? 'max-w-[250px]' : 'max-w-[380px]';

    // Get tooltip position based on viewport and containerRef
    const containerRect = containerRef.current?.getBoundingClientRect();
    const absoluteLeft = containerRect ? containerRect.left + position.x : position.x;
    const absoluteTop = containerRect ? containerRect.top + position.y : position.y;
    const scrollOffsetY = window.scrollY;
    const scrollOffsetX = window.scrollX;
    let scrollTop = 0;
    let scrollLeft = 0;

    if (scrollOffsetY == 0)
      scrollTop = scrollOffsetY + 50

    if (scrollOffsetX != 0)
      scrollLeft = scrollOffsetX - 40

    const left = useMemo(() => {
      const tooltipRightEdge = absoluteLeft + OFFSET_X + TOOLTIP_WIDTH;
      if (tooltipRightEdge > window.innerWidth) {
        return absoluteLeft - TOOLTIP_WIDTH - OFFSET_X - scrollLeft;
      }
      return absoluteLeft + OFFSET_X - scrollLeft;
    }, [absoluteLeft]);

    const top = useMemo(() => {
      const tooltipBottomEdge = absoluteTop + OFFSET_Y + TOOLTIP_HEIGHT;
      if (tooltipBottomEdge > window.innerHeight) {
        return absoluteTop - TOOLTIP_HEIGHT - OFFSET_Y - scrollTop;
      }
      return absoluteTop + OFFSET_Y - scrollTop;
    }, [absoluteTop]);

    useImperativeHandle(ref, () => tooltipRef.current as HTMLDivElement);

    const tooltipContent = (
      <div
        className={`${maxWidth} duration-[325ms] right-0 absolute z-10 bg-white text-black text-sm p-2 rounded box-shadow`}
        style={{ top, left }}
      >
        {matchedCircle && !isEditing ? (
          <>
            <div className="gap-2">
              <button
                onClick={(event) => { event.stopPropagation(); onRemove(matchedCircle) }}
                className="absolute top-0 right-1 bg-white outline-none border-0 hover:outline-none focus:outline-none px-1 pb-0">
                <DeleteIcon />
              </button>
            </div>
            <div className="gap-2">
              <button
                onClick={(event) => { event.stopPropagation(); onEdit(matchedCircle) }}
                className="absolute top-0 right-8 bg-white outline-none border-0 hover:outline-none focus:outline-none px-1 pb-0">
                <EditIcon />
              </button>
            </div>
            <div className="">
              <SelectedColor color={matchedCircle.option} />
              {findIndication &&
                <div className="flex flex-col gap-1 pt-2">
                  <h3 className="font-bold">{findIndication.dimension?.toUpperCase()} DIMENSION</h3>
                  <span className="">{findIndication.instructions}</span>
                </div>
              }
            </div>
          </>
        ) : (
          <React.Fragment>
            <ColorManager step={step} isEditing={isEditing} />
          </React.Fragment>
        )}
      </div>
    );

    return ReactDOM.createPortal(tooltipContent, document.body);
  }
);

export default ColorTooltip;